// Vista del docente: tabla de evaluaciones académicas con `by_area` por
// estudiante (QA-0001-H2-OKR2-B2-A9).
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BookOpenCheck, ChevronRight, FilterX, Loader2 } from "lucide-react";

export default function DocenteResultadosAcademicos() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], areas: [] });
  const [filter, setFilter] = useState("all"); // all | evaluated | pending

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/teacher/academic-evaluations");
        setData(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const visible = data.items.filter((r) => {
    if (filter === "evaluated") return !!r.evaluated_at;
    if (filter === "pending") return !r.evaluated_at;
    return true;
  });
  const evaluatedCount = data.items.filter((r) => r.evaluated_at).length;
  const pendingCount = data.items.length - evaluatedCount;

  const scoreColor = (v) => {
    if (v === null || v === undefined) return "text-[#A6ACC1]";
    if (v >= 80) return "text-[#2A7A36]";
    if (v >= 60) return "text-[#9C7A18]";
    return "text-[#A52258]";
  };

  return (
    <div className="space-y-6" data-testid="docente-resultados-academicos">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#A43790]">Hito 2 · OKR2</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547]">
            Diagnósticos Académicos
          </h1>
          <p className="text-sm text-[#6E7591] mt-1">
            Resultados por área de cada uno de tus estudiantes.
          </p>
        </div>
        <div className="flex gap-2" data-testid="filter-bar">
          {[
            { id: "all", label: "Todos", count: data.items.length },
            { id: "evaluated", label: "Evaluados", count: evaluatedCount },
            { id: "pending", label: "Pendientes", count: pendingCount },
          ].map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 transition-colors ${filter === f.id ? "bg-[#1F2547] border-[#1F2547] text-white" : "bg-white border-[#E1E5F0] text-[#4C5478] hover:border-[#1F2547]"}`}>
              {f.label} <span className="opacity-70 ml-1">({f.count})</span>
            </button>
          ))}
        </div>
      </div>

      <div className="jasq-card overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-[#6E7591]">
            <Loader2 size={16} className="animate-spin" /> Cargando…
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-16 text-[#6E7591]">
            <FilterX size={28} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm font-bold">Sin resultados con el filtro actual.</p>
          </div>
        ) : (
          <table className="w-full min-w-[860px]" data-testid="academic-results-table">
            <thead className="bg-[#F7F8FA] border-b-2 border-[#E1E5F0]">
              <tr>
                <th className="text-left text-[10px] uppercase tracking-wider font-extrabold text-[#6E7591] px-4 py-3">Estudiante</th>
                <th className="text-center text-[10px] uppercase tracking-wider font-extrabold text-[#6E7591] px-3 py-3">Score</th>
                {data.areas.map((area) => (
                  <th key={area} className="text-center text-[10px] uppercase tracking-wider font-extrabold text-[#6E7591] px-3 py-3 whitespace-nowrap">
                    {area.replace(/_/g, " ")}
                  </th>
                ))}
                <th className="text-right text-[10px] uppercase tracking-wider font-extrabold text-[#6E7591] px-4 py-3">Fecha</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.student_id} className="border-b border-[#E1E5F0] last:border-0 hover:bg-[#FAFBFE]"
                  data-testid={`student-row-${r.student_id}`}>
                  <td className="px-4 py-3">
                    <p className="text-sm font-extrabold text-[#1F2547]">{r.student_name || r.student_id}</p>
                    <p className="text-[11px] text-[#6E7591]">{r.student_age ? `${r.student_age} años` : ""}</p>
                  </td>
                  <td className="text-center px-3 py-3">
                    <span className={`text-base font-extrabold ${scoreColor(r.score_total)}`}>
                      {r.score_total ?? "—"}{r.score_total != null && <span className="text-xs">%</span>}
                    </span>
                  </td>
                  {data.areas.map((area) => {
                    const v = r.by_area?.[area];
                    return (
                      <td key={area} className="text-center px-3 py-3">
                        {v === null || v === undefined ? (
                          <span className="text-[#A6ACC1] text-xs" title="Sin preguntas de esta área">—</span>
                        ) : (
                          <span className={`text-sm font-extrabold ${scoreColor(v)}`}>{v}%</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="text-right text-xs text-[#6E7591] px-4 py-3 whitespace-nowrap">
                    {r.evaluated_at ? new Date(r.evaluated_at).toLocaleDateString("es-ES") : <em>No evaluado</em>}
                  </td>
                  <td className="px-3">
                    <Link to={`/docente/estudiante/${r.student_id}/evaluaciones`}
                      className="text-[#A43790] hover:text-[#1F2547] inline-flex items-center text-xs font-bold"
                      data-testid={`history-${r.student_id}`}>
                      Historial <ChevronRight size={12} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-xl border-2 border-[#E1E5F0] bg-white p-4 flex items-start gap-3">
        <BookOpenCheck size={18} className="text-[#A43790] shrink-0 mt-0.5" />
        <p className="text-xs text-[#6E7591]">
          <b className="text-[#1F2547]">Lectura por áreas</b>: cada columna muestra el % de aciertos del estudiante en esa área específica. Una "—" significa que el set generado aleatoriamente no incluyó preguntas de esa área esta vez (se evalúa en próximos intentos).
        </p>
      </div>
    </div>
  );
}
